import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import type { Quote, QuoteDetail, Sale, Unit } from "../types";
import { formatMxn } from "./quoterFormat";
import { QuoteApprovalBadge } from "./QuoteApprovalBadge";
import { QUOTER_QUERY_KEY } from "./quoterQueryKey";
import { useCanDecidePlans } from "./useCanDecidePlans";

// One bounded page: pending plans are few and an approver clears them.
const PENDING_PAGE_SIZE = 100;
const MS_PER_DAY = 86_400_000;

type PendingPlan = {
  quote: Quote;
  detail: QuoteDetail | undefined;
  unit: Unit | undefined;
  requester: Sale | undefined;
};

const unique = (ids: Identifier[]) => [...new Set(ids)];

const usePendingPlans = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: [QUOTER_QUERY_KEY, "plans-to-approve"],
    queryFn: async (): Promise<PendingPlan[]> => {
      const { data: quotes } = await dataProvider.getList<Quote>("quotes", {
        pagination: { page: 1, perPage: PENDING_PAGE_SIZE },
        sort: { field: "created_at", order: "ASC" },
        filter: { approval_status: "pendiente", status: "activa" },
      });
      if (quotes.length === 0) return [];
      const quoteIds = quotes.map((quote) => quote.id);
      const [details, units, sales] = await Promise.all([
        dataProvider.getList<QuoteDetail>("quote_details", {
          pagination: { page: 1, perPage: PENDING_PAGE_SIZE },
          sort: { field: "id", order: "ASC" },
          filter: { "quote_id@in": `(${quoteIds.join(",")})` },
        }),
        dataProvider.getMany<Unit>("units", {
          ids: unique(quotes.map((quote) => quote.unit_id)),
        }),
        dataProvider.getMany<Sale>("sales", {
          ids: unique(quotes.map((quote) => quote.created_by)),
        }),
      ]);
      return quotes.map((quote) => ({
        quote,
        detail: details.data.find((row) => row.quote_id === quote.id),
        unit: units.data.find((row) => row.id === quote.unit_id),
        requester: sales.data.find((row) => row.id === quote.created_by),
      }));
    },
  });
};

const ageInDays = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY));

const PlanRow = ({ plan }: { plan: PendingPlan }) => {
  const { quote, detail, unit, requester } = plan;
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.quoter.approval.${key}`, options);

  const decide = async (action: () => Promise<unknown>, doneKey: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setRejecting(false);
      await queryClient.invalidateQueries({ queryKey: [QUOTER_QUERY_KEY] });
      notify(`crm.quoter.approval.${doneKey}`, { type: "success" });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("action_error"));
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = () => {
    if (comment.trim() === "") {
      setCommentError(t("reject_comment_required"));
      return;
    }
    setCommentError(null);
    return decide(
      () => dataProvider.quoterRejectCustomPlan(quote.id, comment.trim()),
      "rejected_toast",
    );
  };

  const days = ageInDays(quote.created_at);
  return (
    <Card data-testid="plan-to-approve">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium">
            {t("client_unit", {
              client: quote.client_first_name,
              unit: unit?.identifier ?? translate("crm.quoter.unit"),
            })}
            {" · "}
            <span className="tabular-nums">{formatMxn(quote.unit_price)}</span>
          </span>
          <QuoteApprovalBadge quote={quote} />
        </div>
        {detail ? (
          <p className="text-sm tabular-nums">
            {t("plan_summary", {
              down: detail.plan_down_pct,
              monthly: detail.plan_monthly_pct,
              months: detail.plan_months,
              deed: detail.plan_deed_pct,
              discount: detail.plan_discount_pct,
            })}
          </p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          {t("requested_by", {
            name: requester
              ? `${requester.first_name} ${requester.last_name}`.trim()
              : "-",
          })}
          {" · "}
          {days === 0 ? t("age_today") : t("age_days", { days })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            className="h-11 sm:h-9"
            disabled={busy}
            onClick={() =>
              decide(
                () => dataProvider.quoterApproveCustomPlan(quote.id),
                "approved_toast",
              )
            }
          >
            {t("approve")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={busy}
            onClick={() => setRejecting(true)}
          >
            {t("reject")}
          </Button>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </CardContent>

      <Dialog open={rejecting} onOpenChange={(open) => setRejecting(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("reject")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`reject-comment-${quote.id}`}>
              {t("reject_comment_label")}
            </Label>
            <Textarea
              id={`reject-comment-${quote.id}`}
              value={comment}
              maxLength={1000}
              onChange={(e) => setComment(e.target.value)}
            />
            {commentError ? (
              <p role="alert" className="text-sm text-destructive">
                {commentError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => setRejecting(false)}
            >
              {translate("crm.quoter.confirm.back")}
            </Button>
            <Button
              type="button"
              className="h-11 sm:h-9"
              disabled={busy}
              onClick={confirmReject}
            >
              {t("reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const PlansToApproveContent = () => {
  const translate = useTranslate();
  const plans = usePendingPlans();
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <h1 className="text-2xl font-semibold">
        {translate("crm.quoter.approval.list_title")}
      </h1>
      {plans.isError ? (
        <ListErrorState
          message={translate("crm.quoter.approval.list_error")}
          onRetry={() => plans.refetch()}
        />
      ) : plans.isPending ? (
        <ListCardsSkeleton rows={2} />
      ) : (plans.data ?? []).length === 0 ? (
        <ListEmptyState title={translate("crm.quoter.approval.empty")} />
      ) : (
        <ul className="space-y-3" data-testid="plans-to-approve-list">
          {(plans.data ?? []).map((plan) => (
            <li key={plan.quote.id}>
              <PlanRow plan={plan} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Almikia batch EQ3 (TASK-115): "Planes por aprobar". Lists the custom plans pending approval
 * (unit, client, requester, plan summary, age) with "Aprobar" and "Rechazar" (the comment is
 * mandatory). Gated to direccion and the active approvers; anybody else, and an identity without a
 * role, is sent home. The approve / reject RPCs re-check the right in SQL.
 */
export const PlansToApprove = () => {
  const state = useCanDecidePlans();
  if (state === "loading") return null;
  if (state === "denied") return <Navigate to="/" replace />;
  return <PlansToApproveContent />;
};

PlansToApprove.path = "/planes-por-aprobar";
